import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import { ChevronLeft, ChevronRight, FileText, Sparkles } from "lucide-react";
import InsightCard from "@/components/InsightCard";
import ReadinessBar from "@/components/ReadinessBar";
import LogoSpinner from "@/components/LogoSpinner";
import { getAssessment, getQuestions, submitAnswer, generateReport, saveResume } from "@/lib/api";

const READINESS_KEYS = [
  "operational_flexibility",
  "communications_readiness",
  "vendor_recovery",
  "data_protection",
  "staffing_resilience",
];

export default function Assessment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [text, setText] = useState("");
  const [insights, setInsights] = useState([]);
  const [readiness, setReadiness] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const a = await getAssessment(id);
        setAssessment(a.assessment);
        setInsights(a.insights || []);
        setReadiness(a.readiness || {});
        const q = await getQuestions(a.assessment.mode);
        setQuestions(q.questions || []);
        saveResume({ id: a.assessment.id, mode: a.assessment.mode, status: a.assessment.status });
      } catch (e) {
        console.error(e);
        toast.error("Could not load assessment.");
        navigate("/mode");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const current = questions[step];

  // load answer for current step
  useEffect(() => {
    if (!current || !assessment) return;
    setText(assessment.answers?.[current.id] || "");
  }, [current, assessment]);

  const progress = useMemo(() => {
    if (!questions.length) return 0;
    return Math.round(((step + 1) / questions.length) * 100);
  }, [step, questions.length]);

  const saveCurrent = async () => {
    if (!current) return true;
    const value = (text || "").trim();
    if (!value) return true; // skip empty save — proceed
    try {
      setSaving(true);
      const data = await submitAnswer(id, current.id, value);
      setInsights(data.insights || []);
      setReadiness(data.readiness || {});
      setAssessment((a) => ({ ...a, answers: data.answers }));
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Could not save just now — you can keep going. We'll retry on the next step.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    // Always attempt save, but proceed regardless — network blips shouldn't block flow.
    await saveCurrent();
    if (step < questions.length - 1) {
      setStep((s) => s + 1);
    } else {
      try {
        setGenerating(true);
        // Fire-and-forget: backend kicks off LLM in a background task and returns ~instantly.
        // Summary page polls /assessment/{id} for report_status until "ready".
        await generateReport(id);
        saveResume({ id, mode: assessment.mode, status: "completed" });
        navigate(`/summary/${id}`);
      } catch (e) {
        console.error(e);
        toast.error("Could not start report generation. Please retry.");
      } finally {
        setGenerating(false);
      }
    }
  };

  const goBack = async () => {
    // Don't block Back — fire-and-forget the save so the user is never stuck.
    saveCurrent();
    if (step > 0) setStep((s) => s - 1);
  };

  const addSuggestion = (s) => {
    setText((t) => (t ? `${t}, ${s}` : s));
  };

  if (!assessment || !current) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-24 flex flex-col items-center justify-center">
          <LogoSpinner
            size={96}
            label="Loading your assessment…"
            sublabel="Picking up where you left off."
          />
        </main>
      </div>
    );
  }

  const modeLabel = { solopreneur: "Solopreneur", sme: "SME", corporate: "Corporate Department" }[assessment.mode];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="border-b border-slate-200 bg-slate-50/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-[11px] uppercase tracking-[0.14em] text-teal-700">Guided Assessment</span>
            <span className="text-slate-300">·</span>
            <span className="text-sm text-slate-600 truncate">{modeLabel}</span>
          </div>
          <div className="flex items-center gap-3 min-w-[180px]">
            <div className="hidden sm:block text-xs text-slate-500 tabular-nums">
              {step + 1} of {questions.length}
            </div>
            <div className="w-32 sm:w-48 h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-teal-600 transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT: question */}
        <section className="lg:col-span-7">
          <div data-testid="question-card" className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
                Question {step + 1}
              </span>
              <span className="text-slate-300">·</span>
              <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
                {current.category.replace(/_/g, " ")}
              </span>
            </div>

            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-slate-900 leading-snug tracking-tight">
              {current.prompt}
            </h2>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">{current.helper}</p>

            <div className="mt-6">
              <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Your answer</label>
              <textarea
                data-testid="answer-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                placeholder="Write freely — short notes are fine. Tap suggestions below to add them quickly."
                className="mt-2 w-full px-4 py-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors resize-y"
              />
            </div>

            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Suggestions</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {current.examples.map((s, idx) => (
                  <button
                    key={s}
                    onClick={() => addSuggestion(s)}
                    data-testid={`suggestion-${idx}`}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-teal-500/60 hover:text-teal-700 hover:bg-teal-50 transition-colors"
                  >
                    + {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={goBack}
                disabled={step === 0}
                data-testid="back-btn"
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
              <button
                onClick={goNext}
                disabled={saving || generating}
                data-testid="next-btn"
                className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {step === questions.length - 1 ? (
                  <>
                    {generating ? "Generating plan…" : "Generate Plan"} <FileText className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    {saving ? "Saving…" : "Next"} <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </div>

          <p className="mt-4 text-xs text-slate-500">
            Your responses stay in this session. No legal, audit, or certification advice is provided.
          </p>
        </section>

        {/* RIGHT: insights panel */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-20 space-y-6">
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-teal-600" />
                <span className="text-[11px] uppercase tracking-[0.14em] text-slate-600">Operational Insights</span>
              </div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Observations update as you answer. Severity reflects likely impact, not certainty.
              </p>

              <div className="mt-5 space-y-3" data-testid="insights-list">
                {insights.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                    Insights will appear here as you answer.
                  </div>
                ) : (
                  insights.map((ins, idx) => <InsightCard key={`${ins.title}-${idx}`} insight={ins} index={idx} />)
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-600">Readiness Indicators</p>
              <div className="mt-4 space-y-4">
                {READINESS_KEYS.map((k) => (
                  <ReadinessBar key={k} category={k} score={readiness[k] ?? 60} />
                ))}
              </div>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
